import { api } from 'constants/constant';
import { getHeaders } from 'auth/auth';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { documentAPI } from './client/document';
import { adminPipelineAPI } from './admin/pipeline';
import { loanInformationAPI } from './admin/loanInformation';

// Shared onQueryStarted helper for B2B mutations whose backend save runs
// evaluate_and_cache. Invalidates the documentAPI 'updateList' tag so the
// LO Documents tab refetches without a page reload. Tag invalidation is
// scoped to a single RTK Query slice, so we have to dispatch into the
// other slice's util explicitly.
const _invalidateDocsOnSuccess = async (_arg, { dispatch, queryFulfilled }) => {
  try {
    await queryFulfilled;
    dispatch(documentAPI.util.invalidateTags(['updateList']));
  } catch {
    // Mutation failed; existing cache stays valid.
  }
};

const SMART_1003_FIELDS = [
  'firstMortgagePI',
  'otherFinancingPI',
  'hazardInsurance',
  'realEstateTaxes',
  'mortgageInsurance',
  'hoaDues',
  'other',
];

const normalizeHousingExpenseSection = (section = {}) =>
  SMART_1003_FIELDS.reduce((acc, key) => {
    const value = section?.[key];
    acc[key] = typeof value === 'number' ? value : Number.isFinite(Number(value)) ? Number(value) : 0;
    return acc;
  }, {});

const normalizeHousingExpenseResponse = (response) => {
  if (!response || typeof response !== 'object') {
    return response;
  }

  const { data, ...rest } = response;
  if (!data || typeof data !== 'object') {
    return response;
  }

  return {
    ...rest,
    data: {
      present: normalizeHousingExpenseSection(data.present),
      proposed: normalizeHousingExpenseSection(data.proposed),
      dataSources: data.dataSources ?? {},
      presentLegacy: data.present_expense ?? {},
      proposedLegacy: data.proposed_expense ?? {},
    },
  };
};

export const normalizeBorrowerScopeId = (borrowerId) => {
  if (borrowerId === undefined || borrowerId === null) {
    return 'primary';
  }
  const value = String(borrowerId).trim();
  return value || 'primary';
};

const canonicalizeBorrowerCacheKey = (scopeId, result) => {
  const normalizedScope = normalizeBorrowerScopeId(scopeId);
  if (normalizedScope === 'primary') {
    return 'primary';
  }
  const primaryBorrowerId =
    result?.data?.borrower_profile?.data?.personal_details?.userProfileId ||
    result?.data?.borrower_profile?.data?.user_profile?.userProfileId;
  if (primaryBorrowerId && String(primaryBorrowerId) === normalizedScope) {
    return 'primary';
  }
  return normalizedScope;
};

export const borrowerProfileAPI = createApi({
  reducerPath: 'borrower-profile',
  baseQuery: fetchBaseQuery({ baseUrl: api, credentials: 'include' }),
  tagTypes: [
    'BorrowerProfile',
    'ProfileCompletion',
    'EmploymentIncome',
    'Assets',
    'Liabilities',
    'getHousingExpense',
    'TransactionDetails',
    // Per-section tags for AI-triggered fast refresh
    'PersonalDetails',
    'LoanDetails',
    'RealEstate',
    'LiabilitiesLegacy',
    'DeclarationSection',
    'DemographicsSection',
    'getJointSummary',
    // Legacy tags kept for backward compatibility with external consumers
    'checkProfileCompletion',
    'getApplication',
  ],
  endpoints: (builder) => ({
    // Composite init endpoint — returns profile + completion + all dropdowns in 1 request
    getProfileInit: builder.query({
      query: ({ applicationId, borrowerId }) => ({
        params: { borrower_id: normalizeBorrowerScopeId(borrowerId) },
        url: `/b2b/loan/composite/profile/init/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      // 'ProfileCompletion' too: every section save invalidates it, and this
      // response is the only source of the header percentage and sub-tab ticks.
      providesTags: (result, error, { borrowerId }) => [
        { type: 'BorrowerProfile', id: canonicalizeBorrowerCacheKey(borrowerId, result) },
        'ProfileCompletion',
      ],
    }),

    updateStatus: builder.mutation({
      query: ({ applicationId, data }) => {
        return {
          url: `/b2b/loan/application/status_admin/${applicationId}`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication', 'BorrowerProfile', 'ProfileCompletion'],
      // Pipeline list and dashboard live in their own RTK Query slices, so
      // poke them explicitly — same pattern as _invalidateDocsOnSuccess.
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(adminPipelineAPI.util.invalidateTags(['updateAdminPipeline']));
          dispatch(loanInformationAPI.util.invalidateTags(['dashboardData']));
        } catch {
          // Mutation failed; existing cache stays valid.
        }
      },
    }),

    getBorrowerProfile: builder.query({
      query: ({ applicationId, borrowerId }) => {
        return {
          url: `/b2b/loan/dashboard/borrower_profile/${applicationId}`,
          method: 'GET',
          params: borrowerId ? { borrowerId } : undefined,
          headers: getHeaders(),
        };
      },
      providesTags: ['getApplication'],
    }),

    updatePersonalDetails: builder.mutation({
      query: ({ applicationId, data, borrowerId }) => {
        return {
          url: `/b2b/loan/dashboard/personal_details/${applicationId}`,
          method: 'PUT',
          body: data,
          params: borrowerId ? { borrowerId } : undefined,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateDocsOnSuccess,
      invalidatesTags: ['BorrowerProfile', 'PersonalDetails', 'ProfileCompletion', 'getApplication', 'getHousingExpense'],
    }),

    updateLoanDetails: builder.mutation({
      query: ({ applicationId, data }) => {
        return {
          url: `b2b/loan/dashboard/loan/${applicationId}`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateDocsOnSuccess,
      invalidatesTags: ['ProfileCompletion', 'checkProfileCompletion', 'getApplication', 'getHousingExpense', 'TransactionDetails', 'LoanDetails', 'BorrowerProfile'],
    }),

    updateEmployeeAndIncome: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/dashboard/employment_and_income`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateDocsOnSuccess,
      invalidatesTags: ['EmploymentIncome', 'ProfileCompletion', 'getApplication'],
    }),

    updateAssets: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/dashboard/assets`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateDocsOnSuccess,
      invalidatesTags: ['Assets', 'ProfileCompletion', 'getApplication'],
    }),

    updateLiabilities: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/dashboard/liabilities`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateDocsOnSuccess,
      invalidatesTags: ['Liabilities', 'ProfileCompletion', 'getApplication'],
    }),

    updateREOSDetails: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/dashboard/reos`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateDocsOnSuccess,
      invalidatesTags: ['ProfileCompletion', 'getApplication', 'getHousingExpense'],
    }),

    updateDeclaration: builder.mutation({
      query: ({ applicationId, data, borrowerId }) => {
        return {
          url: `b2b/loan/dashboard/declaration/${applicationId}`,
          method: 'PUT',
          body: data,
          params: borrowerId ? { borrowerId } : undefined,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['ProfileCompletion', 'getApplication'],
    }),

    updateDemographics: builder.mutation({
      query: ({ applicationId, data, borrowerId }) => {
        return {
          url: `b2b/loan/dashboard/demographics/${applicationId}`,
          method: 'PUT',
          body: data,
          params: borrowerId ? { borrowerId } : undefined,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['ProfileCompletion', 'getApplication'],
    }),

    createPersonalDetails: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/dashboard/personal_details`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['ProfileCompletion', 'getApplication'],
    }),

    createLoanDetails: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/dashboard/loan`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['ProfileCompletion', 'checkProfileCompletion', 'getApplication', 'LoanDetails', 'BorrowerProfile'],
    }),

    createEmploymentAndIncome: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/dashboard/employment_and_income`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateDocsOnSuccess,
      invalidatesTags: ['EmploymentIncome', 'ProfileCompletion', 'getApplication'],
    }),

    createAssetDetails: builder.mutation({
      query: (data) => {
        return {
          url: `b2b/loan/dashboard/assets`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateDocsOnSuccess,
      invalidatesTags: ['Assets', 'ProfileCompletion', 'getApplication'],
    }),

    createLiabilities: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/dashboard/liabilities`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateDocsOnSuccess,
      invalidatesTags: ['Liabilities', 'ProfileCompletion', 'getApplication'],
    }),

    createREOSData: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/dashboard/reos`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateDocsOnSuccess,
      invalidatesTags: ['ProfileCompletion', 'getApplication', 'getHousingExpense'],
    }),

    createDeclaration: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/dashboard/declaration`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['ProfileCompletion', 'getApplication'],
    }),

    createDemographics: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/dashboard/demographics`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['ProfileCompletion', 'getApplication'],
    }),
    checkProfileCompletion: builder.query({
      query: ({ applicationId }) => {
        return {
          url: `b2b/loan/application/loan_info/complete/${applicationId}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['checkProfileCompletion'],
    }),
    //delete existing dynamic employee
    deleteEmploymentDetails: builder.mutation({
      query: ({ employmentIncomeId, userId }) => {
        return {
          url: `b2b/loan/dashboard/employment_and_income/${employmentIncomeId}?user_id=${userId}`,
          method: 'DELETE',
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['EmploymentIncome', 'getApplication'],
    }),
    //delete existing dynamic liability
    deleteLiabilitiesDetails: builder.mutation({
      query: ({ userLiabilityId, userId }) => {
        return {
          url: `b2b/loan/dashboard/liabilities/${userLiabilityId}?user_id=${userId}`,
          method: 'DELETE',
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['Liabilities', 'getApplication'],
    }),
    //delete existing dynamic REO
    deleteReosDetails: builder.mutation({
      query: ({ reosId, userId }) => {
        return {
          url: `b2b/loan/dashboard/reos/${reosId}?user_id=${userId}`,
          method: 'DELETE',
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication', 'getHousingExpense'],
    }),
    //delete existing dynamic Assets
    deleteAssetsDetails: builder.mutation({
      query: ({ assetId, userId }) => {
        return {
          url: `b2b/loan/dashboard/assets/${assetId}?user_id=${userId}`,
          method: 'DELETE',
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['Assets', 'getApplication'],
    }),

    getBorrowerProfileAdmin: builder.query({
      query: ({ applicationId }) => {
        return {
          url: `/b2b/loan/dashboard/borrower_profile/${applicationId}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['getApplication'],
    }),

    updateEmployeeAndIncomeAdmin: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/loa/employment_and_income`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['EmploymentIncome', 'getApplication'],
    }),

    getEmploymentIncomeProfiles: builder.query({
      query: ({ applicationId, borrowerId }) => {
        return {
          url: `/b2b/loan-management/employment-income/${applicationId}`,
          method: 'GET',
          params: borrowerId ? { borrowerId } : undefined,
          headers: getHeaders(),
        };
      },
      providesTags: ['EmploymentIncome', 'getApplication'],
    }),

    upsertEmploymentIncomeProfiles: builder.mutation({
      query: ({ applicationId, data }) => {
        return {
          url: `/b2b/loan-management/employment-income/${applicationId}`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateDocsOnSuccess,
      invalidatesTags: ['BorrowerProfile', 'EmploymentIncome', 'ProfileCompletion', 'getApplication'],
    }),

    getAssetsAdmin: builder.query({
      query: ({ applicationId, borrowerId }) => {
        return {
          url: `/b2b/loan-management/assets/${applicationId}`,
          method: 'GET',
          params: borrowerId ? { borrowerId } : undefined,
          headers: getHeaders(),
        };
      },
      providesTags: ['Assets', 'getApplication'],
    }),

    upsertAssetsAdmin: builder.mutation({
      query: ({ applicationId, data, borrowerId }) => {
        return {
          url: `/b2b/loan-management/assets/${applicationId}`,
          method: 'PUT',
          body: data,
          params: borrowerId ? { borrowerId } : undefined,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateDocsOnSuccess,
      invalidatesTags: ['Assets', 'ProfileCompletion', 'getApplication'],
    }),

    // ── Per-section lightweight GETs for AI fast refresh ──

    getPersonalDetailsSection: builder.query({
      query: ({ applicationId, borrowerId }) => ({
        url: `/b2b/loans/applications/${applicationId}/personal-details`,
        method: 'GET',
        params: borrowerId ? { borrowerId } : undefined,
        headers: getHeaders(),
      }),
      providesTags: ['PersonalDetails'],
    }),

    getLoanDetailsSection: builder.query({
      query: ({ applicationId }) => ({
        url: `/b2b/loans/applications/${applicationId}/loan-details`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['LoanDetails'],
    }),

    getRealEstateSection: builder.query({
      query: ({ applicationId, borrowerId }) => ({
        url: `/b2b/loans/applications/${applicationId}/real-estate`,
        method: 'GET',
        params: borrowerId ? { borrowerId } : undefined,
        headers: getHeaders(),
      }),
      providesTags: ['RealEstate'],
    }),

    getLiabilitiesLegacySection: builder.query({
      query: ({ applicationId, borrowerId }) => ({
        url: `/b2b/loans/applications/${applicationId}/liabilities-legacy`,
        method: 'GET',
        params: borrowerId ? { borrowerId } : undefined,
        headers: getHeaders(),
      }),
      providesTags: ['LiabilitiesLegacy'],
    }),

    getDeclarationSection: builder.query({
      query: ({ applicationId, borrowerId }) => ({
        url: `/b2b/loans/applications/${applicationId}/declaration-section`,
        method: 'GET',
        params: borrowerId ? { borrowerId } : undefined,
        headers: getHeaders(),
      }),
      providesTags: ['DeclarationSection'],
    }),

    getDemographicsSection: builder.query({
      query: ({ applicationId, borrowerId }) => ({
        url: `/b2b/loans/applications/${applicationId}/demographics-section`,
        method: 'GET',
        params: borrowerId ? { borrowerId } : undefined,
        headers: getHeaders(),
      }),
      providesTags: ['DemographicsSection'],
    }),

    getLiabilitiesAdmin: builder.query({
      query: ({ applicationId, borrowerId }) => {
        return {
          url: `/b2b/loan-management/liabilities/${applicationId}`,
          method: 'GET',
          params: borrowerId ? { borrowerId } : undefined,
          headers: getHeaders(),
        };
      },
      providesTags: ['Liabilities', 'getApplication'],
    }),

    upsertLiabilitiesAdmin: builder.mutation({
      query: ({ applicationId, data, borrowerId }) => {
        return {
          url: `/b2b/loan-management/liabilities/${applicationId}`,
          method: 'PUT',
          body: data,
          params: borrowerId ? { borrowerId } : undefined,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateDocsOnSuccess,
      invalidatesTags: ['Liabilities', 'LiabilitiesLegacy', 'ProfileCompletion', 'getApplication', 'TransactionDetails'],
    }),

    updateREOSDetailsAdmin: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/loa/reos`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateDocsOnSuccess,
      invalidatesTags: ['ProfileCompletion', 'getApplication', 'getHousingExpense'],
    }),

    createEmploymentAndIncomeAdmin: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/loa/employment_and_income`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateDocsOnSuccess,
      invalidatesTags: ['EmploymentIncome', 'getApplication'],
    }),

    // The REO tab's "I don't own any real estate": { applicationId, borrowerId, ownsNoRealEstate }.
    setOwnsNoRealEstateAdmin: builder.mutation({
      query: ({ data }) => ({
        url: `b2b/loan/loa/reos/none`,
        method: 'PUT',
        body: data,
        headers: getHeaders(),
      }),
      onQueryStarted: _invalidateDocsOnSuccess,
      invalidatesTags: ['ProfileCompletion', 'getApplication'],
    }),

    createREOSDataAdmin: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/loa/reos`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateDocsOnSuccess,
      invalidatesTags: ['ProfileCompletion', 'getApplication', 'getHousingExpense'],
    }),

    autoSyncPrimaryReo: builder.mutation({
      query: (data) => ({
        url: 'b2b/loan/loa/reos/auto-sync-primary',
        method: 'POST',
        body: data,
        headers: getHeaders(),
      }),
      invalidatesTags: ['ProfileCompletion', 'getApplication', 'getHousingExpense'],
    }),

    //delete existing dynamic employee
    deleteEmploymentDetailsAdmin: builder.mutation({
      query: ({ employmentIncomeId, userId }) => {
        return {
          url: `b2b/loan/loa/employment_and_income/${employmentIncomeId}?user_id=${userId}`,
          method: 'DELETE',
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['EmploymentIncome', 'getApplication'],
    }),
    //delete existing dynamic liability
    deleteLiabilitiesAdmin: builder.mutation({
      query: ({ applicationId, liabilityId, borrowerId }) => {
        return {
          url: `/b2b/loan-management/liabilities/${applicationId}/${liabilityId}`,
          method: 'DELETE',
          params: borrowerId ? { borrowerId } : undefined,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateDocsOnSuccess,
      invalidatesTags: ['Liabilities', 'LiabilitiesLegacy', 'ProfileCompletion', 'getApplication', 'TransactionDetails'],
    }),
    //delete existing dynamic REO
    deleteReosDetailsAdmin: builder.mutation({
      query: ({ reosId, userId }) => {
        return {
          url: `b2b/loan/loa/reos/${reosId}?user_id=${userId}`,
          method: 'DELETE',
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateDocsOnSuccess,
      invalidatesTags: ['ProfileCompletion', 'getApplication', 'getHousingExpense'],
    }),
    //delete existing dynamic Assets
    deleteAssetsAdmin: builder.mutation({
      query: ({ applicationId, assetId, borrowerId }) => {
        return {
          url: `/b2b/loan-management/assets/${applicationId}/${assetId}`,
          method: 'DELETE',
          params: borrowerId ? { borrowerId } : undefined,
          headers: getHeaders(),
        };
      },
      onQueryStarted: _invalidateDocsOnSuccess,
      invalidatesTags: ['Assets', 'ProfileCompletion', 'getApplication'],
    }),
    softPullCreditReport: builder.mutation({
      query: ({ applicationId, data }) => {
        return {
          url: `/b2b/loan-management/credit/soft-pull/${applicationId}`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      // A pull rewrites credit_report_summary and every borrower's liabilities
      // in the composite, so it has to refresh it — AUS gates on that summary.
      invalidatesTags: ['BorrowerProfile', 'Liabilities', 'LiabilitiesLegacy', 'getApplication'],
    }),
    runMockAus: builder.mutation({
      query: ({ applicationId }) => {
        return {
          url: `/b2b/loans/aus/run/${applicationId}`,
          method: 'POST',
          headers: getHeaders(),
        };
      },
      // The AUS panel reads aus_summary off the composite, which provides
      // 'BorrowerProfile' — not 'getApplication'. Without this the new findings
      // only appeared after a page reload.
      invalidatesTags: ['BorrowerProfile', 'getApplication'],
    }),
    getHousingExpense: builder.query({
      query: ({ userId }) => {
        return {
          url: `b2b/loan/dashboard/housing_expense/${userId}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      transformResponse: normalizeHousingExpenseResponse,
      providesTags: ['getHousingExpense'],
    }),
    getHousingExpenseOfficer: builder.query({
      query: ({ userId }) => {
        return {
          url: `b2b/loan/loa/housing_expense/${userId}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      transformResponse: normalizeHousingExpenseResponse,
      providesTags: ['getHousingExpense'],
    }),

    // Application-scoped on purpose: DTI, affordability inputs and the
    // representative credit score belong to the loan, not to the borrower
    // currently selected in the profile tabs.
    getJointSummary: builder.query({
      query: ({ applicationId }) => ({
        url: `b2b/loans/joint-summary/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data ?? null,
      providesTags: ['getJointSummary'],
    }),

    saveHousingExpense: builder.mutation({
      query: ({ data, userId }) => {
        return {
          url: `b2b/loan/dashboard/housing_expense/${userId}`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      transformResponse: normalizeHousingExpenseResponse,
      invalidatesTags: ['getHousingExpense'],
    }),

    saveHousingExpenseOfficer: builder.mutation({
      query: ({ data, userId }) => {
        return {
          url: `b2b/loan/loa/housing_expense/${userId}`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      transformResponse: normalizeHousingExpenseResponse,
      // The form reads the profile init payload, and the save recalculates the
      // transaction details, so both have to refetch or the old values come back.
      invalidatesTags: ['getHousingExpense', 'ProfileCompletion', 'getApplication', 'TransactionDetails'],
    }),

    getTransactionDetailsOfficer: builder.query({
      async queryFn({ applicationId }, _queryApi, _extraOptions, baseQuery) {
        if (!applicationId) {
          return { data: { success: false, data: null } };
        }

        const result = await baseQuery({
          url: `b2b/loan/dashboard/transaction_details/${applicationId}`,
          method: 'GET',
          headers: getHeaders(),
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data };
      },
      providesTags: ['TransactionDetails'],
    }),
    saveTransactionDetailsOfficer: builder.mutation({
      query: ({ applicationId, data }) => {
        return {
          url: `b2b/loan/dashboard/transaction_details/${applicationId}`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['TransactionDetails'],
    }),
    getTransactionDetails: builder.query({
      async queryFn({ applicationId }, _queryApi, _extraOptions, baseQuery) {
        if (!applicationId) {
          return { data: { success: false, data: null } };
        }

        const result = await baseQuery({
          url: `b2b/loan/dashboard/transaction_details/${applicationId}`,
          method: 'GET',
          headers: getHeaders(),
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data };
      },
      providesTags: ['TransactionDetails'],
    }),
    saveTransactionDetails: builder.mutation({
      query: ({ applicationId, data }) => {
        return {
          url: `b2b/loan/dashboard/transaction_details/${applicationId}`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
    }),

    getIncomeAnalytics: builder.query({
      query: ({ userId }) => {
        return {
          url: `b2b/ai/income-analytics?user_id=${userId}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getIncomeDocumentStatus: builder.query({
      async queryFn({ applicationId }, _queryApi, _extraOptions, baseQuery) {
        if (!applicationId) {
          return { data: { success: false, data: null } };
        }

        const result = await baseQuery({
          url: `b2b/loan/income_analysis/check_income_document?applicationId=${applicationId}`,
          method: 'GET',
          headers: getHeaders(),
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data };
      },
    }),

    updateIncomeAnalytics: builder.mutation({
      query: ({ updatedIncomeData }) => {
        return {
          url: `b2b/loan/income_analysis/update_income_analytics`,
          method: 'PUT',
          body: updatedIncomeData,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getIncomeAnalytics'],
    }),

    //truv integration
    createTruvIntegration: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/truv_api/bridge-token`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
    }),

    getNewTruvLinkId: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/truv_api/link-id`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
    }),

    getFullTruvReport: builder.mutation({
      query: ({ employmentRecordId, forceRefresh = false }) => {
        return {
          url: `b2b/loan/truv_api/report/${employmentRecordId}`,
          method: 'GET',
          params: { forceRefresh },
          headers: getHeaders(),
        };
      },
    }),

    getTruvEvents: builder.query({
      query: ({ employmentRecordId, limit = 20 }) => {
        return {
          url: `b2b/loan/truv_api/events/${employmentRecordId}`,
          method: 'GET',
          params: { limit },
          headers: getHeaders(),
        };
      },
    }),

    getEmploymentDetails: builder.query({
      query: ({ linkId }) => {
        return {
          url: `b2b/loan/truv_api/links/${linkId}/employment`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    createEmploymentReport: builder.mutation({
      query: ({ userId }) => {
        console.log('data', userId);
        return {
          url: `b2b/loan/truv_api/users/${userId}/reports`,
          method: 'POST',
          body: {},
          headers: getHeaders(),
        };
      },
    }),

    // Create a new chat session
    createNewChat: builder.mutation({
      query: ({ metadata, borrower_id, loan_application_id } = {}) => {
        const body = {};
        if (borrower_id) body.borrower_id = borrower_id;
        if (loan_application_id) body.loan_application_id = loan_application_id;
        if (metadata) body.metadata = metadata;
        return {
          url: `b2b/ai/chat/sessions`,
          method: 'POST',
          headers: getHeaders(),
          body,
        };
      },
      transformResponse: (response) => ({
        chat_id: response?.id ?? response?.chat_id,
        title: response?.title,
        ...response,
      }),
    }),

    // List chat sessions – uses lightweight endpoint that accepts user_id as query param
    getAllChats: builder.query({
      query: ({ userId, tenantId } = {}) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (tenantId) params.append('tenant_id', tenantId);
        return {
          url: `b2b/ai/chat/user-sessions?${params.toString()}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      transformResponse: (response) => {
        const sessions = Array.isArray(response) ? response : [];
        return {
          chats: sessions.map((s) => ({
            chat_id: s.id,
            title: s.title,
            start_time: s.created_at,
            last_message_at: s.last_message_at,
            borrower_id: s.borrower_id,
            loan_application_id: s.loan_application_id,
          })),
        };
      },
    }),

    // Fetch chat messages for a session
    getChatMessages: builder.query({
      query: ({ sessionId, userId } = {}) => {
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        return {
          url: `b2b/ai/chat/user-sessions/${sessionId}/messages?${params.toString()}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      transformResponse: (response) => {
        const messages = Array.isArray(response) ? response : [];
        return {
          messages: messages.map((m) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            created_at: m.created_at,
            feedback: m.metadata?.feedback ?? null,
            versions: m.metadata?.versions ?? null,
          })),
        };
      },
    }),

    GetTruvVerify: builder.query({
      query: ({ email }) => {
        return {
          url: `b2b/loan/truv_api/verified-report?email=${email}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),


    // Step 1: Generate Plaid Link Token
    createPlaidLinkToken: builder.mutation({
      query: ({ client_user_id, client_phone_number }) => ({
        url: `b2b/loan/plaid/create_link_token`,
        method: 'POST',
        body: {
          client_user_id,
          client_phone_number,
        },
        headers: getHeaders(),
      }),
    }),

    // Step 2: Exchange Public Token
    exchangePlaidPublicToken: builder.mutation({
      query: ({ public_token, user_profile_id }) => ({
        url: `b2b/loan/plaid/exchange_token`,  // updated endpoint
        method: 'POST',
        body: {
          public_token,
          user_profile_id,
        },
        headers: getHeaders(),
      }),
    }),
    //User Profile for the plaid setup
    getPlaidAccounts: builder.query({
      query: (userProfileId) => ({
        url: `b2b/loan/plaid/accounts/${userProfileId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),
    //Indentity for the plaid setup
    getPlaidIdentity: builder.query({
      query: (userProfileId) => ({
        url: `b2b/loan/plaid/identity/${userProfileId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    // B2B Dropdown Endpoints
    getMartialStatus: builder.query({
      query: () => ({
        url: `/b2b/loan/application_dropdowns/marital_status`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    getCitizenshipStatus: builder.query({
      query: () => ({
        url: `/b2b/loan/application_dropdowns/citizenship_status`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    getOwnerShip: builder.query({
      query: () => ({
        url: `/b2b/loan/application_dropdowns/ownership`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    getStates: builder.query({
      query: () => ({
        url: `/b2b/loan/application_dropdowns/states`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    getEthnicity: builder.query({
      query: () => ({
        url: `/b2b/loan/application_dropdowns/ethnicity`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    getUserRace: builder.query({
      query: () => ({
        url: `/b2b/loan/application_dropdowns/race`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    getLiabilityType: builder.query({
      query: () => ({
        url: `/b2b/loan/application_dropdowns/liability_type`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    getAssetType: builder.query({
      query: () => ({
        url: `/b2b/loan/application_dropdowns/asset_type`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    getLoanProgram: builder.query({
      query: () => ({
        url: `/b2b/loan/application_dropdowns/loan_program`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    getLoanTermYears: builder.query({
      query: () => ({
        url: `/b2b/loan/application_dropdowns/loan_term`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    getOccupancy: builder.query({
      query: () => ({
        url: `/b2b/loan/application_dropdowns/occupancy`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    getLoanType: builder.query({
      query: () => ({
        url: `/b2b/loan/application_dropdowns/loan_type`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    getLoanPurpose: builder.query({
      query: () => ({
        url: `/b2b/loan/application_dropdowns/loan_purpose`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    getMannerHeld: builder.query({
      query: () => ({
        url: `/b2b/loan/application_dropdowns/manner_held`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    getPropertyRight: builder.query({
      query: () => ({
        url: `/b2b/loan/application_dropdowns/property_right`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    getPropertyType: builder.query({
      query: () => ({
        url: `/b2b/loan/application_dropdowns/property_type`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    getRefinancePurpose: builder.query({
      query: () => ({
        url: `/b2b/loan/application_dropdowns/refinance_purpose`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    getCountiesByState: builder.query({
      query: (stateId) => ({
        url: `/b2b/loan/application_dropdowns/counties/${stateId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    getEmploymentType: builder.query({
      query: () => ({
        url: `/b2b/loan/application_dropdowns/employment_type`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    getIncomeType: builder.query({
      query: () => ({
        url: `/b2b/loan/application_dropdowns/income_type`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    getApplicationStatus: builder.query({
      query: () => ({
        url: `/b2b/loan/application_dropdowns/application_status`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    // Bundle endpoint — replaces 20 individual dropdown calls with 1
    getDropdownBundle: builder.query({
      query: () => ({
        url: `/b2b/loan/application_dropdowns/bundle`,
        method: 'GET',
        headers: getHeaders(),
      }),
      keepUnusedDataFor: Infinity,
    }),

  }),
});

export const {
  useGetProfileInitQuery,
  useCheckProfileCompletionQuery,
  useUpdateStatusMutation,
  //get api export LO
  useGetBorrowerProfileQuery,
  //update api export LO
  useUpdatePersonalDetailsMutation,
  useUpdateLoanDetailsMutation,
  useUpdateEmployeeAndIncomeMutation,
  useUpdateAssetsMutation,
  useUpdateLiabilitiesMutation,
  useUpdateREOSDetailsMutation,
  useUpdateDeclarationMutation,
  useUpdateDemographicsMutation,
  //create api export LO
  useCreatePersonalDetailsMutation,
  useCreateLoanDetailsMutation,
  useCreateAssetDetailsMutation,
  useCreateEmploymentAndIncomeMutation,
  useCreateREOSDataMutation,
  useCreateLiabilitiesMutation,
  useCreateDeclarationMutation,
  useCreateDemographicsMutation,
  //delete api export LO
  useDeleteEmploymentDetailsMutation,
  useDeleteLiabilitiesDetailsMutation,
  useDeleteReosDetailsMutation,
  useDeleteAssetsDetailsMutation,
  //get api export LOA
  useGetBorrowerProfileAdminQuery,
  useGetEmploymentIncomeProfilesQuery,
  //create api export LOA
  useCreateEmploymentAndIncomeAdminMutation,
  useCreateREOSDataAdminMutation,
  useSetOwnsNoRealEstateAdminMutation,
  //update api export LOA
  useUpdateEmployeeAndIncomeAdminMutation,
  useUpsertEmploymentIncomeProfilesMutation,
  useGetAssetsAdminQuery,
  useUpsertAssetsAdminMutation,
  // Per-section lightweight GETs for AI fast refresh
  useGetPersonalDetailsSectionQuery,
  useGetLoanDetailsSectionQuery,
  useGetRealEstateSectionQuery,
  useGetLiabilitiesLegacySectionQuery,
  useGetDeclarationSectionQuery,
  useGetDemographicsSectionQuery,
  useGetLiabilitiesAdminQuery,
  useUpsertLiabilitiesAdminMutation,
  useSoftPullCreditReportMutation,
  useRunMockAusMutation,
  useUpdateREOSDetailsAdminMutation,
  //delete api export LOA
  useDeleteEmploymentDetailsAdminMutation,
  useDeleteAssetsAdminMutation,
  useDeleteLiabilitiesAdminMutation,
  useDeleteReosDetailsAdminMutation,
  useAutoSyncPrimaryReoMutation,
  //Housing expense api export
  useGetHousingExpenseQuery,
  useGetHousingExpenseOfficerQuery,
  useGetJointSummaryQuery,
  useSaveHousingExpenseMutation,
  useSaveHousingExpenseOfficerMutation,
  //transaction details api export
  useGetTransactionDetailsOfficerQuery,
  useGetTransactionDetailsQuery,
  useSaveTransactionDetailsOfficerMutation,
  useSaveTransactionDetailsMutation,
  // income analyt get api export
  useGetIncomeAnalyticsQuery,
  useGetIncomeDocumentStatusQuery,
  useUpdateIncomeAnalyticsMutation,
  //truv integration
  useCreateTruvIntegrationMutation,
  useGetNewTruvLinkIdMutation,
  useGetFullTruvReportMutation,
  useGetEmploymentDetailsQuery,
  useCreateEmploymentReportMutation,
  // Plaid integration
  useCreatePlaidLinkTokenMutation,
  useExchangePlaidPublicTokenMutation,
  useGetPlaidAccountsQuery,
  useGetPlaidIdentityQuery,


  //ChatHistory
  useGetAllChatsQuery,
  useGetChatMessagesQuery,
  useCreateNewChatMutation,
  useGetTruvVerifyQuery,
  useGetTruvEventsQuery,
  // B2B Dropdown hooks
  useGetMartialStatusQuery,
  useGetCitizenshipStatusQuery,
  useGetOwnerShipQuery,
  useGetStatesQuery,
  useGetEthnicityQuery,
  useGetUserRaceQuery,
  useGetLiabilityTypeQuery,
  useGetAssetTypeQuery,
  useGetLoanProgramQuery,
  useGetLoanTermYearsQuery,
  useGetOccupancyQuery,
  useGetLoanTypeQuery,
  useGetLoanPurposeQuery,
  useGetMannerHeldQuery,
  useGetPropertyRightQuery,
  useGetPropertyTypeQuery,
  useGetRefinancePurposeQuery,
  useGetCountiesByStateQuery,
  useGetEmploymentTypeQuery,
  useGetIncomeTypeQuery,
  useGetApplicationStatusQuery,
  useGetDropdownBundleQuery,
} = borrowerProfileAPI;

/** The server's own name for the file, off `Content-Disposition`. */
const filenameFrom = (response, fallback) => {
  const header = response.headers.get('Content-Disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  return match ? decodeURIComponent(match[1]) : fallback;
};

/**
 * Fetch the URLA as a rendered PDF.
 *
 * Binary `application/pdf`, so it cannot be an RTK query. The same shape as
 * the MISMO export fetches (`redux/services/mismo`), and for the same
 * reasons: auth here is HttpOnly cookies, so `credentials: 'include'` is
 * required and a bare `window.open` gets a 401 even with the right URL; and a
 * bare path resolves against the *frontend* origin, where the dev server
 * answers with `index.html` and the request never reaches the backend at all.
 * `getHeaders()` carries the CSRF token; its JSON `Content-Type` is dropped
 * because this request has no body.
 *
 * The signature is the viewer's client-side mock — nothing on the server has it
 * — so it travels with the request. Without it the download would be the one
 * copy of the document that is missing what the officer just signed. The
 * adopted initials ride along for the same reason and are stamped alongside it.
 *
 * Returns the filename alongside the blob so the caller does not have to fetch
 * the form JSON just to name the download. Resolving that JSON costs the server
 * the same expensive read as rendering the document itself, and asking for it
 * twice to open one modal is most of the wait.
 *
 * `signal` lets a superseded request be abandoned — adopting a signature
 * re-renders the document, and the render it replaces should stop rather than
 * run on to completion against a viewer that has moved on.
 */
export const fetchUrlaDocument = async ({
  applicationId,
  borrowerId,
  signature,
  signal,
} = {}) => {
  const headers = getHeaders();
  delete headers['Content-Type'];

  const params = new URLSearchParams();
  if (borrowerId) params.set('borrowerId', normalizeBorrowerScopeId(borrowerId));
  if (signature?.name) {
    params.set('signatureName', signature.name);
    // Initials are optional on the adopt card, so they are sent only when one
    // was adopted — an empty parameter would have the server stamp a blank
    // mark where the form prints an initials line.
    if (signature.initials) params.set('signatureInitials', signature.initials);
    if (signature.signedAt) params.set('signedAt', signature.signedAt);
  }
  const query = params.toString();

  const response = await fetch(
    `${api}/b2b/loan/urla/${applicationId}/document${query ? `?${query}` : ''}`,
    { method: 'GET', credentials: 'include', headers, signal }
  );

  if (!response.ok) {
    // The server sends a JSON envelope on failure, not a PDF. Read the reason
    // rather than handing the user a corrupt download.
    let detail = `The server returned ${response.status}.`;
    try {
      const body = await response.json();
      if (body?.detail) detail = body.detail;
    } catch {
      /* not JSON — keep the status line */
    }
    throw new Error(detail);
  }

  return {
    blob: await response.blob(),
    filename: filenameFrom(response, `${applicationId}-loan-application.pdf`),
  };
};
